<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import KraftchatAIAPI from 'dashboard/api/kraftchatAI';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import { useAlert } from 'dashboard/composables';

import KraftchatAIAutonomySettings from './KraftchatAIAutonomySettings.vue';
import KraftchatAIHandoffSettings from './KraftchatAIHandoffSettings.vue';
import KraftchatAIHandoffTriggersSettings from './KraftchatAIHandoffTriggersSettings.vue';
import KraftchatAIRulesSummaryCard from './KraftchatAIRulesSummaryCard.vue';
import KraftchatAISafetySettings from './KraftchatAISafetySettings.vue';
import KraftchatAIToolsSettings from './KraftchatAIToolsSettings.vue';

import {
  buildPolicyPayload,
  countEnabledValues,
  defaultAgentPolicy,
  getAutonomyLabel,
  handoffTriggerOptions,
  rulesContent,
  safetyOptions,
  toolActionOptions,
} from '../../helpers/kraftchatAIRulesConfig';

const route = useRoute();
const agents = ref([]);
const policies = ref([]);
const selectedAgentId = ref('');
const policy = ref({ ...defaultAgentPolicy });
const openSection = ref('autonomy');
const isLoading = ref(false);
const isSaving = ref(false);
const loadError = ref('');

const selectedAgent = computed(() =>
  agents.value.find(agent => String(agent.id) === String(selectedAgentId.value))
);

const selectedPolicy = computed(() =>
  policies.value.find(
    item => String(item.ai_agent_id) === String(selectedAgentId.value)
  )
);

const hasAgents = computed(() => agents.value.length > 0);

// Lets KraftchatAIInboxList's "Definir regras" action land straight on the
// right agent (?agent_id=123) instead of whichever one happens to be
// first. Ignored if it doesn't match a real agent on this account, so a
// stale or tampered query param falls back to the normal default.
const preselectedAgentId = computed(() => {
  const requestedId = route.query.agent_id;
  if (!requestedId) return null;

  const match = agents.value.find(
    agent => String(agent.id) === String(requestedId)
  );
  return match ? String(match.id) : null;
});

const agentOptions = computed(() =>
  agents.value.map(agent => ({
    label: `${agent.name} — ${agent.inbox_name}`,
    value: String(agent.id),
  }))
);

const selectedAgentLabel = computed(() => {
  return (
    agentOptions.value.find(
      option => String(option.value) === String(selectedAgentId.value)
    )?.label || rulesContent.agentPlaceholder
  );
});

const autonomySummary = computed(() => {
  const replyStatus = policy.value.auto_reply_enabled
    ? 'resposta automática ativa'
    : 'resposta automática desativada';

  return `${getAutonomyLabel(policy.value.autonomy_level)} · ${replyStatus}`;
});

const handoffSummary = computed(() => {
  return policy.value.human_handoff_enabled
    ? 'transferência ativa'
    : 'transferência desativada';
});

const triggersSummary = computed(() => {
  const enabledCount = policy.value.handoff_triggers?.length || 0;
  return `${enabledCount} de ${handoffTriggerOptions.length} ativos`;
});

const toolsSummary = computed(() => {
  return `${countEnabledValues(policy.value.allowed_actions)} de ${
    toolActionOptions.length
  } ativas`;
});

const safetySummary = computed(() => {
  return `${countEnabledValues(policy.value.safety_settings)} de ${
    safetyOptions.length
  } ativos`;
});

const normalizedPolicy = source => ({
  ...defaultAgentPolicy,
  ...source,
  ai_agent_id: selectedAgentId.value,
  allowed_actions: {
    ...defaultAgentPolicy.allowed_actions,
    ...(source?.allowed_actions || {}),
  },
  safety_settings: {
    ...defaultAgentPolicy.safety_settings,
    ...(source?.safety_settings || {}),
  },
  handoff_triggers:
    source?.handoff_triggers || defaultAgentPolicy.handoff_triggers,
});

const hydratePolicy = () => {
  policy.value = normalizedPolicy(selectedPolicy.value);
};

const loadRules = async () => {
  isLoading.value = true;
  loadError.value = '';

  try {
    const [agentsResponse, policiesResponse] = await Promise.all([
      KraftchatAIAPI.getAgents(),
      KraftchatAIAPI.getAgentPolicies(),
    ]);

    agents.value = agentsResponse.data.payload || [];
    policies.value = policiesResponse.data.payload || [];

    if (!selectedAgentId.value) {
      selectedAgentId.value = String(
        preselectedAgentId.value || agents.value[0]?.id || ''
      );
    }

    hydratePolicy();
  } catch (error) {
    loadError.value =
      error.response?.data?.message || error.message || rulesContent.loadError;
  } finally {
    isLoading.value = false;
  }
};

const toggleSection = section => {
  openSection.value = openSection.value === section ? '' : section;
};

const handleSave = async () => {
  if (!selectedAgent.value) {
    return;
  }

  isSaving.value = true;

  try {
    const payload = buildPolicyPayload({
      ...policy.value,
      ai_agent_id: selectedAgent.value.id,
    });

    const response = selectedPolicy.value
      ? await KraftchatAIAPI.updateAgentPolicy(selectedPolicy.value.id, payload)
      : await KraftchatAIAPI.createAgentPolicy(payload);

    const savedPolicy = response.data.payload;

    policies.value = [
      ...policies.value.filter(item => item.id !== savedPolicy.id),
      savedPolicy,
    ];

    policy.value = normalizedPolicy(savedPolicy);

    useAlert(rulesContent.saveSuccess);
  } catch (error) {
    useAlert(
      error.response?.data?.message || error.message || rulesContent.saveError
    );
  } finally {
    isSaving.value = false;
  }
};

onMounted(loadRules);

watch(selectedAgentId, hydratePolicy);
</script>

<template>
  <section class="rounded-xl border border-n-weak bg-n-solid-1 p-6 shadow-sm">
    <div class="max-w-5xl space-y-5">
      <div
        class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between"
      >
        <div class="max-w-3xl space-y-1">
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ rulesContent.title }}
          </h2>

          <p class="text-sm leading-6 text-n-slate-11">
            {{ rulesContent.description }}
          </p>
        </div>

        <div v-if="hasAgents" class="flex min-w-72 flex-col gap-1">
          <label class="mb-0.5 text-heading-3 text-n-slate-12">
            {{ rulesContent.agentLabel }}
          </label>

          <SelectMenu
            v-model="selectedAgentId"
            class="!w-full [&>button]:!h-10 [&>button]:!w-full [&>button]:!max-w-none [&>button]:!justify-between [&>button]:!px-3 [&>div]:!w-full [&>div]:!max-w-none"
            :label="selectedAgentLabel"
            :options="agentOptions"
            sub-menu-position="bottom"
          />
        </div>
      </div>

      <p
        v-if="isLoading"
        class="rounded-xl border border-dashed border-n-weak bg-n-background p-4 text-sm text-n-slate-11"
      >
        {{ rulesContent.loadingLabel }}
      </p>

      <p
        v-else-if="loadError"
        class="rounded-xl border border-n-weak bg-n-background p-4 text-sm text-n-ruby-11"
      >
        {{ loadError }}
      </p>

      <div
        v-else-if="!hasAgents"
        class="rounded-xl border border-n-weak bg-n-background p-4"
      >
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ rulesContent.emptyAgentTitle }}
        </h3>

        <p class="mt-1 text-sm leading-6 text-n-slate-11">
          {{ rulesContent.emptyAgentDescription }}
        </p>
      </div>

      <div v-else class="space-y-3">
        <KraftchatAIRulesSummaryCard
          :title="rulesContent.autonomyTitle"
          :description="rulesContent.autonomyDescription"
          :summary="autonomySummary"
          :is-open="openSection === 'autonomy'"
          @toggle="toggleSection('autonomy')"
        >
          <KraftchatAIAutonomySettings
            :policy="policy"
            @update:policy="policy = $event"
          />
        </KraftchatAIRulesSummaryCard>

        <KraftchatAIRulesSummaryCard
          :title="rulesContent.handoffTitle"
          :description="rulesContent.handoffDescription"
          :summary="handoffSummary"
          :is-open="openSection === 'handoff'"
          @toggle="toggleSection('handoff')"
        >
          <KraftchatAIHandoffSettings
            :policy="policy"
            @update:policy="policy = $event"
          />
        </KraftchatAIRulesSummaryCard>

        <KraftchatAIRulesSummaryCard
          :title="rulesContent.triggersTitle"
          :description="rulesContent.triggersDescription"
          :summary="triggersSummary"
          :is-open="openSection === 'triggers'"
          @toggle="toggleSection('triggers')"
        >
          <KraftchatAIHandoffTriggersSettings
            :policy="policy"
            @update:policy="policy = $event"
          />
        </KraftchatAIRulesSummaryCard>

        <KraftchatAIRulesSummaryCard
          :title="rulesContent.toolsTitle"
          :description="rulesContent.toolsDescription"
          :summary="toolsSummary"
          :is-open="openSection === 'tools'"
          @toggle="toggleSection('tools')"
        >
          <KraftchatAIToolsSettings
            :policy="policy"
            @update:policy="policy = $event"
          />
        </KraftchatAIRulesSummaryCard>

        <KraftchatAIRulesSummaryCard
          :title="rulesContent.safetyTitle"
          :description="rulesContent.safetyDescription"
          :summary="safetySummary"
          :is-open="openSection === 'safety'"
          @toggle="toggleSection('safety')"
        >
          <KraftchatAISafetySettings
            :policy="policy"
            @update:policy="policy = $event"
          />
        </KraftchatAIRulesSummaryCard>

        <div class="flex justify-end pt-2">
          <NextButton
            type="button"
            :label="
              isSaving ? rulesContent.savingLabel : rulesContent.saveLabel
            "
            :is-loading="isSaving"
            @click="handleSave"
          />
        </div>
      </div>
    </div>
  </section>
</template>
